import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Play, Sparkles, Loader2, Boxes, Shuffle, ShieldCheck, Trophy, Clapperboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Cast3DDialog } from "@/components/Cast3DViewer";
import { GameTrailer } from "@/components/GameTrailer";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Public pre-login landing for a shared game link (/g/:id). Shows the auto-generated
// promo card + a real gameplay clip + a Play / Sign-up CTA so first-time visitors
// convert before the login wall.
export default function ShareLanding() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [castModel, setCastModel] = useState(null);
  const [clip, setClip] = useState(null);
  const [clipRank, setClipRank] = useState(null);
  const [cinematicLive, setCinematicLive] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch(`${BACKEND}/api/og/game/${id}/info`)
      .then((r) => r.json())
      .then((d) => { if (alive) { setInfo(d); setLoading(false); } })
      .catch(() => { if (alive) { setInfo({ found: false }); setLoading(false); } });
    return () => { alive = false; };
  }, [id]);

  useEffect(() => {
    let alive = true;
    fetch(`${BACKEND}/api/clips?game_id=${id}&limit=1&sort=trending`)
      .then((r) => r.json())
      .then((d) => { if (alive) setClip((d.clips || [])[0] || null); })
      .catch(() => {});
    fetch(`${BACKEND}/api/clips/leaderboard?days=7&limit=5`)
      .then((r) => r.json())
      .then((d) => { if (alive) { const hit = (d.clips || []).find((c) => c.game_id === id); if (hit) setClipRank(hit.rank); } })
      .catch(() => {});
    fetch(`${BACKEND}/api/cinematic/config`)
      .then((r) => r.json())
      .then((d) => { if (alive) setCinematicLive(!!d.enabled); })
      .catch(() => {});
    return () => { alive = false; };
  }, [id]);

  const rankLabel = clipRank ? (clipRank === 1 ? "#1 clip this week" : `#${clipRank} clip this week`) : null;

  return (
    <div className="min-h-screen bg-[#05060a] text-white flex flex-col items-center justify-center p-6" data-testid="share-landing">
      <img src="/brand/gv-wordmark.png" alt="GameView Studio" className="h-9 mb-8 opacity-90" />
      {loading ? (
        <Loader2 className="w-8 h-8 animate-spin text-[#7c5cff]" data-testid="share-landing-loading" />
      ) : info?.found ? (
        <div className="w-full max-w-xl">
          {info.trailer_clip_url ? (
            <div className="rounded-2xl overflow-hidden border border-white/10 bg-black shadow-2xl" data-testid="share-landing-trailer-clip">
              <video src={`${BACKEND}${info.trailer_clip_url}`} autoPlay muted loop playsInline controls className="w-full block bg-black" style={{ aspectRatio: "16/9" }} />
            </div>
          ) : (
            <GameTrailer
              image={`${BACKEND}/api/og/game/${id}/image.png`}
              bgmUrl={info.bgm_url}
              emoji={info.cover_emoji}
              className="shadow-2xl"
              testid="share-landing-trailer"
            />
          )}
          <h1 className="text-3xl font-extrabold mt-6 text-center" data-testid="share-landing-title">{info.cover_emoji} {info.title}</h1>
          <p className="text-white/60 text-center mt-1">by {info.owner_name}</p>
          {cinematicLive && (
            <div className="flex justify-center mt-3" data-testid="share-cinematic-ribbon">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold bg-gradient-to-r from-amber-400 to-orange-500 text-black shadow">
                <Clapperboard className="w-3.5 h-3.5" /> Playable in AAA Unreal Cinematic Mode
              </span>
            </div>
          )}
          {info.licence?.verified && (
            <p className="text-center text-emerald-400 text-xs font-semibold mt-2 flex items-center justify-center gap-1.5" data-testid="share-verified-badge">
              <ShieldCheck className="w-3.5 h-3.5" /> Verified original · {info.licence.licence_label || "All Rights Reserved"}
            </p>
          )}
          {info.remix_count > 0 && (
            <p className="text-center text-[#7c5cff] text-xs font-semibold mt-1.5 flex items-center justify-center gap-1.5" data-testid="share-remix-count">
              <Shuffle className="w-3.5 h-3.5" /> Remixed {info.remix_count} time{info.remix_count === 1 ? "" : "s"} by the community
            </p>
          )}
          {info.description && <p className="text-white/70 text-center mt-3 max-w-lg mx-auto">{info.description}</p>}

          {clip && (
            <div className="mt-6" data-testid="share-clip">
              {rankLabel && (
                <div className="flex justify-center mb-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold bg-amber-400 text-black shadow" data-testid="share-clip-rank">
                    <Trophy className="w-3.5 h-3.5 fill-current" /> {rankLabel}
                  </span>
                </div>
              )}
              <div className="rounded-2xl overflow-hidden border border-white/10 bg-black">
                <video src={`${BACKEND}${clip.url}`} autoPlay muted loop playsInline controls className="w-full block bg-black" style={{ aspectRatio: "16/9" }} data-testid="share-clip-video" />
              </div>
              <p className="text-center text-white/40 text-[11px] mt-2">🎬 Real gameplay, captured by a player</p>
            </div>
          )}

          {(info.cast_3d || []).length > 0 && (
            <div className="mt-6" data-testid="share-cast-3d">
              <div className="flex items-center justify-center gap-2 mb-3">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold text-white bg-gradient-to-r from-[#7c3aed] to-[#3b9eff]">
                  <Boxes className="w-3.5 h-3.5" /> AI-built 3D cast
                </span>
              </div>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                {info.cast_3d.map((m) => (
                  <button key={m.asset_id} onClick={() => m.glb && setCastModel(m)} disabled={!m.glb} data-testid={`share-cast-${m.asset_id}`} className={`group text-center ${m.glb ? "cursor-pointer" : "cursor-default"}`}>
                    <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden border border-white/10 bg-white/5">
                      <img src={m.thumbnail} alt={m.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                      {m.glb && <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center"><Boxes className="w-5 h-5 text-white" /></div>}
                    </div>
                    <div className="text-[10px] text-white/50 mt-1 max-w-[80px] truncate mx-auto">{m.role}</div>
                  </button>
                ))}
              </div>
              {(info.cast_3d || []).some((m) => m.glb) && <p className="text-center text-white/40 text-[11px] mt-2">Tap a character to spin it in 3D</p>}
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-7">
            <Button onClick={() => navigate(`/app/arcade/${id}`)} className="rounded-full h-12 px-7 text-base bg-gradient-to-r from-[#1f6feb] to-[#7c3aed]" data-testid="share-landing-play">
              <Play className="w-5 h-5" /> Play now — free
            </Button>
            <Button onClick={() => navigate("/register")} variant="outline" className="rounded-full h-12 px-7 text-base border-white/20 text-white hover:bg-white/10" data-testid="share-landing-signup">
              <Sparkles className="w-5 h-5" /> Build your own with AI
            </Button>
          </div>
          {cinematicLive && (
            <p className="text-center text-amber-300/80 text-xs mt-3 flex items-center justify-center gap-1.5" data-testid="share-cinematic-hint">
              <Clapperboard className="w-3.5 h-3.5" /> Tip: open the game and tap <b>Cinematic Mode</b> to stream it in AAA Unreal quality.
            </p>
          )}
          <p className="text-center text-white/40 text-xs mt-6">Made with AI on GameView Studio — prompt it, play it, publish it.</p>
        </div>
      ) : (
        <div className="text-center max-w-md" data-testid="share-landing-missing">
          <h1 className="text-2xl font-bold">This game isn't available</h1>
          <p className="text-white/60 mt-2">It may be private or no longer published.</p>
          <Button onClick={() => navigate("/")} className="rounded-full mt-5 bg-gradient-to-r from-[#1f6feb] to-[#7c3aed]" data-testid="share-landing-home">
            <Sparkles className="w-4 h-4" /> Explore GameView Studio
          </Button>
        </div>
      )}
      <Cast3DDialog model={castModel} gameId={id} onClose={() => setCastModel(null)} />
    </div>
  );
}
